import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { MapProps } from './types';
import i18n from 'i18next';
import { markerContentHtml, destinationTooltipHtml, memberTooltipHtml, myLocationHtml } from './markerStyle';
import ctl from './mapControls.module.css';
// Leaflet 이 직접 붙이는 클래스에 우리 톤을 입힙니다 (전역 — 해시하면 안 걸립니다)
import './LeafletMap.css';

/**
 * 해외 미니 지도 — Leaflet + OSM 타일.
 *
 * 지도 두 갈래 중 하나입니다.
 *   국내(미니·전체)  → KakaoMap
 *   해외(미니·전체)  → 여기
 *
 * 예전에는 해외 전체지도를 구글(Maps JavaScript API)로 띄웠는데, 웹뷰 안에서
 * 구글 지도를 쓰면 지도 로드마다 과금됩니다. 앱에서는 네이티브 지도로 넘기고
 * 브라우저에서는 무료인 OSM 을 씁니다.
 *
 * 마커 생김새는 둘 다 markerStyle 한 곳에서 가져옵니다.
 * 예전에는 여기만 Leaflet 기본 파란 핀이라 국내·해외 지도의 모양이 달랐고,
 * 핀 이미지를 unpkg.com 에서 받아오기까지 했습니다.
 */
export const LeafletMapSimple: React.FC<MapProps> = ({
    center,
    markers = [],
    routes = [],
    height = 300,
    zoomLevel = 4,
    fitBounds = false,
    showMyLocationBtn,
    focusTarget,
    viewAllTrigger,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const polylineLayersRef = useRef<L.Polyline[]>([]);
    const fitAppliedRef = useRef(false);
    const myLocRef = useRef<L.Marker | null>(null);

    /* 카카오 level(작을수록 확대) ↔ Leaflet zoom(클수록 확대). level 4 = zoom 15. */
    const toZoom = (lv: number) => Math.max(3, Math.min(19, 19 - lv));

    // 지도 초기화 — 마운트 시 1회만
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const map = L.map(containerRef.current, {
            attributionControl: false,
            zoomControl: false,
        }).setView([center.lat, center.lng], toZoom(zoomLevel));

        L.control.zoom({ position: 'bottomright' }).addTo(map);
        L.control.attribution({ position: 'bottomleft', prefix: false })
            .addAttribution('© OpenStreetMap')
            .addTo(map);

        /* CARTO 는 이제 basemap 에 키를 요구해서, 키 없이 불러오면
           타일에 API KEY REQUIRED 워터마크가 찍힙니다. OSM 기본 타일로 바꿉니다 —
           키가 필요 없고 사용 조건도 표기만 하면 됩니다. */
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
        }).addTo(map);

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // center 변경 시 지도 재생성 대신 view만 이동 (마커/경로 유지)
    useEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.setView([center.lat, center.lng], mapRef.current.getZoom());
    }, [center.lat, center.lng]);

    // 마커 업데이트
    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current;

        // 기존 마커 레이어 제거
        map.eachLayer((layer) => {
            if (layer instanceof L.Marker) layer.remove();
        });

        markers.forEach(m => {
            /* 카카오·구글과 같은 마커 HTML 을 divIcon 으로 넣습니다.
               크기는 markerStyle 이 정하므로 iconSize 는 재지 않고 내용에 맡깁니다. */
            const icon = L.divIcon({
                html: markerContentHtml(m),
                className: '',          // Leaflet 기본 흰 네모 배경 제거
                iconSize: undefined,
                iconAnchor: m.rating != null && !m.isDestination ? [0, 0] : [18, 36],
            });
            const marker = L.marker([m.lat, m.lng], { icon }).addTo(map);
            if (m.tooltip) {
                marker.bindTooltip(
                    m.isDestination ? destinationTooltipHtml(m.tooltip) : memberTooltipHtml(m.tooltip),
                    { permanent: m.isDestination, direction: 'top', offset: [0, -34], className: 'dokocrew-map-tip' },
                );
            }
        });
    }, [markers]);

    // 폴리라인 (경로) 업데이트
    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current;

        polylineLayersRef.current.forEach(p => p.remove());
        polylineLayersRef.current = [];

        routes.forEach(r => {
            if (!r.coordinates || r.coordinates.length < 2) return;
            const line = L.polyline(r.coordinates as [number, number][], {
                color: r.color || '#4285F4',
                weight: r.strokeWidth || 4,
                opacity: 0.85,
            }).addTo(map);
            polylineLayersRef.current.push(line);
        });

        // 자동 범위 조정 — 최초 1회만 (사용자 줌/팬 보존)
        if (fitBounds && !fitAppliedRef.current) {
            const bounds = L.latLngBounds([]);
            markers.forEach(m => bounds.extend([m.lat, m.lng]));
            routes.forEach(r => r.coordinates?.forEach(c => bounds.extend(c as [number, number])));
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [40, 40] });
                fitAppliedRef.current = true;
            }
        } else if (!fitBounds) {
            fitAppliedRef.current = false;
        }
    }, [routes, markers, fitBounds]);

    /** 마커·경로 전체가 보이도록 맞춥니다. */
    const fitAll = () => {
        const map = mapRef.current;
        if (!map) return;
        const b = L.latLngBounds([]);
        markers.forEach(m => b.extend([m.lat, m.lng]));
        routes.forEach(r => r.coordinates?.forEach(c => b.extend(c as [number, number])));
        if (b.isValid()) map.fitBounds(b, { padding: [40, 40] });
    };

    // 전체 보기 트리거 — 값이 오를 때마다 다시 맞춥니다.
    useEffect(() => {
        if (!viewAllTrigger) return;
        fitAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewAllTrigger]);

    // 특정 좌표로 포커스
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !focusTarget) return;
        map.setView(
            [focusTarget.lat, focusTarget.lng],
            typeof focusTarget.level === 'number' ? toZoom(focusTarget.level) : map.getZoom(),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusTarget?.key]);

    const showMyLocation = () => {
        const map = mapRef.current;
        if (!navigator.geolocation || !map) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const at: [number, number] = [pos.coords.latitude, pos.coords.longitude];
                myLocRef.current?.remove();
                myLocRef.current = L.marker(at, {
                    icon: L.divIcon({
                        html: `<div style="transform:translate(-50%,-50%);display:inline-block;">${myLocationHtml()}</div>`,
                        className: '',
                        iconSize: undefined,
                        iconAnchor: [0, 0],
                    }),
                }).addTo(map);
                map.setView(at, map.getZoom());
            },
            () => { /* 권한 거부 — 조용히 넘깁니다 */ },
            { enableHighAccuracy: true, timeout: 8000 },
        );
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: typeof height === 'number' ? `${height}px` : height }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {showMyLocationBtn && (
                <button
                    type="button"
                    onClick={showMyLocation}
                    className={`${ctl['mc-btn']} ${ctl['mc-bottom-right-high']}`}
                    title={i18n.t('mapUi.currentLocation')}
                    aria-label={i18n.t('mapUi.currentLocation')}
                >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                    </svg>
                </button>
            )}
        </div>
    );
};
